<script setup>
import { ref, computed, defineProps } from "vue"
import { useI18n } from "vue-i18n"
import { formatCurrency } from "@common/js/helpers"
import { CheckIcon, XMarkIcon as XMarkIconMini } from "@heroicons/vue/20/solid"

const props = defineProps({
  consumptionRegime: {
    required: false,
    type: String,
    default: "",
  },
  offers: {
    type: Array,
    required: true,
  },
})

const { t } = useI18n()

const pricing = computed(() => {
  return {
    tiers: [
      {
        name: "Injection en totalite",
        id: "tier-total-resale-rate",
        featured: props.consumptionRegime == "FULL_SALE",
        description: "",
      },
      {
        name: "Autoconsommation avec injection du surplus",
        id: "tier-partial-self-consumption-rate",
        featured: props.consumptionRegime == "PARTIAL_SELF_CONSUMPTION",
        description: "",
      },
      {
        name: "Prime",
        id: "tier-prime",
        featured: false,
        description: "",
      },
    ],
    sections: props.offers.map((o) => {
      return {
        name: t(
          `common.administration.resale-agreements.offers.section-headers.${o.id}`
        ),
        features: [
          {
            name: "≤ 3 kWc",
            tiers: [
              `${formatCurrency(
                o.fullResaleLte3kWc,
                "fr-FR",
                "EUR",
                4
              )}  / kWh`,
              `${formatCurrency(
                o.partialSelfConsumptionLte3kWc,
                "fr-FR",
                "EUR",
                4
              )}  / kWh`,
              `${formatCurrency(o.subsidyLte3kWc, "fr-FR", "EUR", 0)}  / kWc`,
            ],
          },
          {
            name: "≤ 9 kWc",
            tiers: [
              `${formatCurrency(
                o.fullResaleLte9kWc,
                "fr-FR",
                "EUR",
                4
              )}  / kWh`,
              `${formatCurrency(
                o.partialSelfConsumptionLte9kWc,
                "fr-FR",
                "EUR",
                4
              )}  / kWh`,
              `${formatCurrency(o.subsidyLte9kWc, "fr-FR", "EUR", 0)}  / kWc`,
            ],
          },
          {
            name: "≤ 36 kWc",
            tiers: [
              `${formatCurrency(
                o.fullResaleLte36kWc,
                "fr-FR",
                "EUR",
                4
              )}  / kWh`,
              `${formatCurrency(
                o.partialSelfConsumptionLte100kWc,
                "fr-FR",
                "EUR",
                4
              )}  / kWh`,
              `${formatCurrency(o.subsidyLte36kWc, "fr-FR", "EUR", 0)}  / kWc`,
            ],
          },
          {
            name: "≤ 100 kWc",
            tiers: [
              `${formatCurrency(
                o.fullResaleLte100kWc,
                "fr-FR",
                "EUR",
                4
              )}  / kWh`,
              `${formatCurrency(
                o.partialSelfConsumptionLte100kWc,
                "fr-FR",
                "EUR",
                4
              )}  / kWh`,
              `${formatCurrency(o.subsidyLte100kWc, "fr-FR", "EUR", 0)}  / kWc`,
            ],
          },
          {
            name: "≤ 200 kWc",
            tiers: [
              `${formatCurrency(
                o.fullResaleLte200kWc,
                "fr-FR",
                "EUR",
                4
              )}  / kWh`,
              `${formatCurrency(
                o.partialSelfConsumptionLte200kWc,
                "fr-FR",
                "EUR",
                4
              )}  / kWh`,
              "Non éligible",
            ],
          },
          {
            name: "≤ 500 kWc",
            tiers: [
              `${formatCurrency(
                o.fullResaleLt500kWc,
                "fr-FR",
                "EUR",
                4
              )}  / kWh *`,
              `${formatCurrency(
                o.partialSelfConsumptionLt500kWc,
                "fr-FR",
                "EUR",
                4
              )}  / kWh`,
              "Non éligible",
            ],
          },
        ],
      }
    }),
  }
})
</script>

<template>
  <div class="bg-white">
    <main>
      <!-- Pricing section -->
      <div class="isolate overflow-hidden">
        <div class="relative bg-gray-50">
          <div class="mx-auto max-w-7xl px-6 lg:px-8 pb-5">
            <!-- Feature comparison (up to lg) -->
            <section
              aria-labelledby="mobile-comparison-heading"
              class="lg:hidden"
            >
              <h2 id="mobile-comparison-heading" class="sr-only">
                Feature comparison
              </h2>

              <div class="mx-auto max-w-2xl space-y-16">
                <div
                  v-for="(tier, tierIdx) in pricing.tiers"
                  :key="tier.id"
                  class="border-t border-gray-900/10"
                >
                  <div
                    :class="[
                      tier.featured
                        ? 'border-indigo-600'
                        : 'border-transparent',
                      '-mt-px w-72 border-t-2 pt-10 md:w-80',
                    ]"
                  >
                    <h3
                      :class="[
                        tier.featured ? 'text-indigo-600' : 'text-gray-900',
                        'text-sm font-semibold leading-6',
                      ]"
                    >
                      {{ tier.name }}
                    </h3>
                    <p class="mt-1 text-sm leading-6 text-gray-600">
                      {{ tier.description }}
                    </p>
                  </div>

                  <div class="mt-10 space-y-10">
                    <div
                      v-for="section in pricing.sections"
                      :key="section.name"
                    >
                      <h4 class="text-sm font-semibold leading-6 text-gray-900">
                        {{ section.name }}
                      </h4>
                      <div class="relative mt-6">
                        <!-- Fake card background -->
                        <div
                          aria-hidden="true"
                          class="absolute inset-y-0 right-0 hidden w-1/2 rounded-lg bg-white shadow-sm sm:block"
                        />

                        <div
                          :class="[
                            tier.featured
                              ? 'ring-2 ring-indigo-600'
                              : 'ring-1 ring-gray-900/10',
                            'relative rounded-lg bg-white shadow-sm sm:rounded-none sm:bg-transparent sm:shadow-none sm:ring-0',
                          ]"
                        >
                          <dl
                            class="divide-y divide-gray-200 text-sm leading-6"
                          >
                            <div
                              v-for="feature in section.features"
                              :key="feature.name"
                              class="flex items-center justify-between px-4 py-3 sm:grid sm:grid-cols-2 sm:px-0"
                            >
                              <dt class="pr-4 text-gray-600">
                                {{ feature.name }}
                              </dt>
                              <dd
                                class="flex items-center justify-end sm:justify-center sm:px-4"
                              >
                                <span
                                  v-if="
                                    typeof feature.tiers[tierIdx] === 'string'
                                  "
                                  :class="
                                    tier.featured
                                      ? 'font-semibold text-indigo-600'
                                      : 'text-gray-900'
                                  "
                                  >{{ feature.tiers[tierIdx] }}</span
                                >
                                <template v-else>
                                  <CheckIcon
                                    v-if="feature.tiers[tierIdx] === true"
                                    class="mx-auto h-5 w-5 text-indigo-600"
                                    aria-hidden="true"
                                  />
                                  <XMarkIconMini
                                    v-else
                                    class="mx-auto h-5 w-5 text-gray-400"
                                    aria-hidden="true"
                                  />
                                  <span class="sr-only">{{
                                    feature.tiers[tierIdx] === true
                                      ? "Yes"
                                      : "No"
                                  }}</span>
                                </template>
                              </dd>
                            </div>
                          </dl>
                        </div>

                        <!-- Fake card border -->
                        <div
                          aria-hidden="true"
                          :class="[
                            tier.featured
                              ? 'ring-2 ring-indigo-600'
                              : 'ring-1 ring-gray-900/10',
                            'pointer-events-none absolute inset-y-0 right-0 hidden w-1/2 rounded-lg sm:block',
                          ]"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- Feature comparison (lg+) -->
            <section
              aria-labelledby="comparison-heading"
              class="hidden lg:block"
            >
              <h2 id="comparison-heading" class="sr-only">
                Feature comparison
              </h2>

              <div
                class="grid grid-cols-4 gap-x-8 border-t border-gray-900/10 before:block"
              >
                <div
                  v-for="tier in pricing.tiers"
                  :key="tier.id"
                  aria-hidden="true"
                  class="-mt-px"
                >
                  <div
                    :class="[
                      tier.featured
                        ? 'border-indigo-600'
                        : 'border-transparent',
                      'border-t-2 pt-10',
                    ]"
                  >
                    <p
                      :class="[
                        tier.featured ? 'text-indigo-600' : 'text-gray-900',
                        'text-sm font-semibold leading-6',
                      ]"
                    >
                      {{ tier.name }}
                    </p>
                    <p class="mt-1 text-sm leading-6 text-gray-600">
                      {{ tier.description }}
                    </p>
                  </div>
                </div>
              </div>

              <div class="-mt-6 space-y-16">
                <div v-for="section in pricing.sections" :key="section.name">
                  <h3 class="text-sm font-semibold leading-6 text-gray-900">
                    {{ section.name }}
                  </h3>
                  <div class="relative -mx-8 mt-10">
                    <!-- Fake card backgrounds -->
                    <div
                      class="absolute inset-x-8 inset-y-0 grid grid-cols-4 gap-x-8 before:block"
                      aria-hidden="true"
                    >
                      <div
                        class="h-full w-full rounded-lg bg-white shadow-sm"
                      />
                      <div
                        class="h-full w-full rounded-lg bg-white shadow-sm"
                      />
                      <div
                        class="h-full w-full rounded-lg bg-white shadow-sm"
                      />
                    </div>

                    <table
                      class="relative w-full border-separate border-spacing-x-8"
                    >
                      <thead>
                        <tr class="text-left">
                          <th scope="col">
                            <span class="sr-only">Feature</span>
                          </th>
                          <th
                            v-for="tier in pricing.tiers"
                            :key="tier.id"
                            scope="col"
                          >
                            <span class="sr-only">{{ tier.name }} tier</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(feature, featureIdx) in section.features"
                          :key="feature.name"
                        >
                          <th
                            scope="row"
                            class="w-1/4 py-3 pr-4 text-left text-sm font-normal leading-6 text-gray-900"
                          >
                            {{ feature.name }}
                            <div
                              v-if="featureIdx !== section.features.length - 1"
                              class="absolute inset-x-8 mt-3 h-px bg-gray-200"
                            />
                          </th>
                          <td
                            v-for="(tier, tierIdx) in pricing.tiers"
                            :key="tier.id"
                            class="relative w-1/4 px-4 py-0 text-center"
                          >
                            <span class="relative h-full w-full py-3">
                              <span
                                v-if="
                                  typeof feature.tiers[tierIdx] === 'string'
                                "
                                :class="[
                                  tier.featured
                                    ? 'font-semibold text-indigo-600'
                                    : 'text-gray-900',
                                  'text-sm leading-6',
                                ]"
                                >{{ feature.tiers[tierIdx] }}</span
                              >
                              <template v-else>
                                <CheckIcon
                                  v-if="feature.tiers[tierIdx] === true"
                                  class="mx-auto h-5 w-5 text-indigo-600"
                                  aria-hidden="true"
                                />
                                <XMarkIconMini
                                  v-else
                                  class="mx-auto h-5 w-5 text-gray-400"
                                  aria-hidden="true"
                                />
                                <span class="sr-only">{{
                                  feature.tiers[tierIdx] === true ? "Yes" : "No"
                                }}</span>
                              </template>
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <!-- Fake card borders -->
                    <div
                      class="pointer-events-none absolute inset-x-8 inset-y-0 grid grid-cols-4 gap-x-8 before:block"
                      aria-hidden="true"
                    >
                      <div
                        v-for="tier in pricing.tiers"
                        :key="tier.id"
                        :class="[
                          tier.featured
                            ? 'ring-2 ring-indigo-600'
                            : 'ring-1 ring-gray-900/10',
                          'rounded-lg',
                        ]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
          <p class="text-center p-5 mt-1 text-sm leading-6 text-gray-600">
            * 0,1208 € / kWh en dessous de 1100 kWh / kWc pour T1 2024 puis
            evolue par trimestre (N) selon indexation indeces INSEE (k) : 0,1208
            x K (n + p) ? K (n)
          </p>
        </div>
      </div>
    </main>
  </div>
</template>
